import { hasProductPackages } from '@/lib/package-utils'
import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { MyDatePicker } from '@/components/MyDatePicker'
import AppSelect from '@/components/zthd/app-select'
import {
  DropdownCountryCombined,
  createCountryStore,
  createCountryMultiStore,
} from '@/components/DropdownCountry'
import DimensionSelect from './DimensionSelect'
import useAdmobStore, { useAdmobDatePickerStore } from '@/stores/admob-store'
import { DropdownPackageName } from '@/components/DropdownPackage'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/hooks'
import { isReadable } from '@/hooks/auth'
import AdmobContrastModeSelector from './AdmobContrastModeSelector'
import { useContrastModeStore } from '@/components/roi/ContrastModeSelector'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'

const useAdmobCountryStore = createCountryStore('total', 'admob')
const useAdmobCountriesStore = createCountryMultiStore()

const PAM_MANAGEMENT_USERS = [
  '2f3d7a88', //lzm
  'cg5bg1b9', //lqy
  '2426ca7f', //cxn
  '3ff237cf', //wnp
  '972c2g2c', //zl
  '549g3eae', //wyy
  'g15g221e', // ry
  'g17442g3', // hhh
]

const areCountriesEqual = (a = [], b = []) => {
  if (a.length !== b.length) return false
  return a.every((value, index) => value === b[index])
}

function AdmobReportFilters({
  onExport,
  onExportPam,
  onCheckRcAdmob,
  onOpenManualCheck,
  isMutating,
  isPamMutating,
  isRcAuditMutating,
}) {
  const navigate = useNavigate()
  const { filters, setFilter } = useAdmobStore()
  const selectedCountry = useAdmobCountryStore((state) => state.value)
  const selectedCountries = useAdmobCountriesStore((state) => state.countries)
  const user = useAuthStore((state) => state.auth?.user)
  const isPAMUser = PAM_MANAGEMENT_USERS.includes(user?.id || '')
  const pamReads = (user?.pamReadable || '').split(',').map((read) => read.trim())
  const multiCountries = (filters.countries || []).filter(
    (country) => country && country !== 'total',
  )
  const pamCountries = multiCountries.length
    ? multiCountries
    : [filters.country && filters.country !== 'total' ? filters.country : '*']
  const canExportPam = /^[1-9]\d*$/.test(filters.pid) && pamCountries.every(
    (country) => isReadable(pamReads, filters.pid, country),
  )
  const isDimensionEnabled =
    filters.pid &&
    filters.pid !== 'total' &&
    (!hasProductPackages(filters.pid) || filters.package_name !== 'total')

  // 获取对比模式状态
  const contrastEnabled = useContrastModeStore((state) => state.enabled)
  const showContrastValue = useContrastModeStore(
    (state) => state.showContrastValue,
  )
  const showDiff = useContrastModeStore((state) => state.showDiff)
  const showDiffPercent = useContrastModeStore(
    (state) => state.showDiffPercent,
  )
  const setShowContrastValue = useContrastModeStore(
    (state) => state.setShowContrastValue,
  )
  const setShowDiff = useContrastModeStore((state) => state.setShowDiff)
  const setShowDiffPercent = useContrastModeStore(
    (state) => state.setShowDiffPercent,
  )

  useEffect(() => {
    if (filters.country !== selectedCountry) {
      setFilter('country', selectedCountry)
    }
  }, [filters.country, selectedCountry, setFilter])

  useEffect(() => {
    if (!areCountriesEqual(filters.countries || [], selectedCountries || [])) {
      setFilter('countries', selectedCountries || [])
    }
  }, [filters.countries, selectedCountries, setFilter])

  return (
    <div className="flex flex-col gap-4 p-4 border-b">
      <div className="flex items-center space-x-4 flex-wrap gap-4">
        <MyDatePicker
          datePickerStore={useAdmobDatePickerStore}
          showMaxDate={true}
        />
        <AppSelect
          value={filters.pid}
          onValueChange={(value) => {
            setFilter('pid', value)
            setFilter('package_name', 'total')
            setFilter('ad_unit', [])
            setFilter('app_version', [])
            setFilter('format', [])
          }}
        />
      {hasProductPackages(filters.pid) && (
        <DropdownPackageName
          pid={filters.pid}
          value={filters.package_name}
          update={(value) => {
            setFilter('package_name', value)
            setFilter('ad_unit', [])
            setFilter('app_version', [])
            setFilter('format', [])
          }}
        />
      )}
      <DropdownCountryCombined
        useSingleStore={useAdmobCountryStore}
        useMultiStore={useAdmobCountriesStore}
      />
      {isDimensionEnabled && (
        <>
          <DimensionSelect
            label={'App Version'}
            dimension="app_version"
            value={filters.app_version || []}
            onValueChange={(value) => setFilter('app_version', value)}
            pid={filters.pid}
            packageName={filters.package_name}
            multiple
          />
          <DimensionSelect
            label={'Ad Unit'}
            dimension="ad_unit"
            value={filters.ad_unit || []}
            onValueChange={(value) => setFilter('ad_unit', value)}
            pid={filters.pid}
            packageName={filters.package_name}
            multiple
          />
          <DimensionSelect
            label={'Format'}
            dimension="format"
            value={filters.format || []}
            onValueChange={(value) => setFilter('format', value)}
            pid={filters.pid}
            packageName={filters.package_name}
            multiple
          />
        </>
      )}
        {/* 对比模式选择器 */}
        <AdmobContrastModeSelector />
        <div className="flex items-center space-x-2">
          <Button onClick={onExport} disabled={isMutating} variant="outline">
            Export{isMutating && 'ing...'}
          </Button>
          <Button
            onClick={onExportPam}
            disabled={isPamMutating || !canExportPam}
            title={canExportPam ? undefined : '未开通当前产品及国家的 PAM 只读权限'}
            variant="outline"
          >
            Export PAM{isPamMutating && 'ing...'}
          </Button>
          <Button
            onClick={onCheckRcAdmob}
            disabled={isRcAuditMutating}
            variant="outline"
          >
            {isRcAuditMutating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking RC...
              </>
            ) : (
              'Check RC AdMob'
            )}
          </Button>
          <Button onClick={onOpenManualCheck} variant="outline">
            Manual Check
          </Button>
          {isPAMUser && (
            <Button onClick={() => navigate('/admob/pam')} variant="outline">
              Mediation A/B
            </Button>
          )}
        </div>
      </div>
      {/* 对比模式显示选项，仅在对比模式启用时显示 */}
      {contrastEnabled && (
        <div className="flex items-center gap-4 flex-wrap sm:gap-2">
          <div className="flex items-center gap-3 text-xs flex-nowrap">
            <div className="flex items-center gap-2">
              <Checkbox
                id="show-contrast-value"
                checked={showContrastValue}
                onCheckedChange={setShowContrastValue}
              />
              <Label htmlFor="show-contrast-value" className="cursor-pointer">
                原始值
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="show-diff"
                checked={showDiff}
                onCheckedChange={setShowDiff}
              />
              <Label htmlFor="show-diff" className="cursor-pointer">
                差异值
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="show-diff-percent"
                checked={showDiffPercent}
                onCheckedChange={setShowDiffPercent}
              />
              <Label htmlFor="show-diff-percent" className="cursor-pointer">
                差异比例
              </Label>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdmobReportFilters
